import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getPublicTournamentsApi } from "../api/tournament.api";

import Input from "../components/common/Input";
import Pagination from "../components/common/Pagination";
import Icon from "../components/common/Icon";

import { toJalaliFull } from "../utils/jalali";
import {
  TOURNAMENT_TYPE_LABELS,
  TOURNAMENT_STATUS_LABELS,
} from "../utils/constants";

const STATUS_FILTERS = [
  { value: "", label: "همه وضعیت‌ ها" },
  { value: "ONGOING", label: "در حال برگزاری" },
  { value: "OPEN", label: "باز برای ثبت‌ نام" },
  { value: "COMPLETED", label: "پایان‌ یافته" },
  { value: "CLOSED", label: "بسته شده" },
];

const STATUS_THEME = {
  OPEN: {
    ring: "ring-blue-400/30",
    glow: "bg-blue-500/[0.08]",
    chip: "bg-blue-50 text-blue-700 border-blue-100",
    dot: "bg-blue-500",
    bar: "from-blue-400 via-blue-500 to-lime-400",
  },
  ONGOING: {
    ring: "ring-emerald-400/30",
    glow: "bg-emerald-500/[0.10]",
    chip: "bg-emerald-50 text-emerald-700 border-emerald-100",
    dot: "bg-emerald-500",
    bar: "from-emerald-400 via-emerald-500 to-lime-400",
  },
  COMPLETED: {
    ring: "ring-slate-300/40",
    glow: "bg-slate-400/[0.06]",
    chip: "bg-slate-100 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
    bar: "from-slate-300 via-slate-400 to-slate-300",
  },
  CLOSED: {
    ring: "ring-red-300/30",
    glow: "bg-red-500/[0.06]",
    chip: "bg-red-50 text-red-600 border-red-100",
    dot: "bg-red-500",
    bar: "from-red-300 via-red-400 to-red-300",
  },
};

function TournamentCard({ tournament, onClick }) {
  const theme = STATUS_THEME[tournament.status] || STATUS_THEME.OPEN;
  const clubsCount = tournament._count?.registrations ?? 0;
  const matchesCount = tournament._count?.matches ?? 0;

  return (
    <article
      onClick={onClick}
      className={`group relative cursor-pointer overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white shadow-sm ring-1 ring-transparent transition-all duration-500 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-slate-900/[0.12] hover:${theme.ring}`}
    >
      {/* top accent bar */}
      <div
        className={`h-1.5 w-full bg-gradient-to-l ${theme.bar}`}
      />

      <div
        className={`pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full ${theme.glow} blur-3xl transition duration-700 group-hover:scale-125`}
      />

      <div className="relative p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#0B1F17] to-[#12452F] text-emerald-300 shadow-lg">
              <Icon name="trophy" className="h-7 w-7" />
            </div>

            <div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black text-slate-500">
                {TOURNAMENT_TYPE_LABELS[tournament.type] || tournament.type}
              </span>
            </div>
          </div>

          <span
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-black ${theme.chip}`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${theme.dot} ${
                tournament.status === "ONGOING" ? "animate-pulse" : ""
              }`}
            />
            {TOURNAMENT_STATUS_LABELS[tournament.status] || tournament.status}
          </span>
        </div>

        <h3 className="mt-5 truncate text-xl font-black text-slate-800 transition group-hover:text-emerald-600 sm:text-2xl">
          {tournament.name}
        </h3>

        {tournament.description && (
          <p className="mt-2 line-clamp-2 text-xs leading-6 text-slate-400 sm:text-sm">
            {tournament.description}
          </p>
        )}

        <div className="mt-5 flex items-center gap-2 text-xs font-bold text-slate-400">
          <Icon name="calendar" className="h-4 w-4 text-slate-300" />
          {toJalaliFull(tournament.startDate)}
          {tournament.endDate && (
            <>
              <span className="text-slate-300">تا</span>
              {toJalaliFull(tournament.endDate)}
            </>
          )}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2 border-t border-dashed border-slate-100 pt-5">
          <div className="flex items-center gap-2.5 rounded-xl bg-slate-50/80 p-2.5">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-600">
              <Icon name="shield" className="h-4 w-4" />
            </div>
            <div>
              <strong className="block text-sm font-black text-slate-800">
                {clubsCount.toLocaleString("fa-IR")}
              </strong>
              <span className="text-[10px] text-slate-400">باشگاه</span>
            </div>
          </div>

          <div className="flex items-center gap-2.5 rounded-xl bg-slate-50/80 p-2.5">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-600">
              <Icon name="ball" className="h-4 w-4" />
            </div>
            <div>
              <strong className="block text-sm font-black text-slate-800">
                {matchesCount.toLocaleString("fa-IR")}
              </strong>
              <span className="text-[10px] text-slate-400">مسابقه</span>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-1.5 rounded-xl bg-[#0B1F17] py-2.5 text-xs font-black text-white transition group-hover:bg-emerald-600">
          مشاهده جزئیات و جدول
          <Icon name="chevronLeft" className="h-4 w-4" />
        </div>
      </div>
    </article>
  );
}

export default function Tournaments() {
  const navigate = useNavigate();

  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    getPublicTournamentsApi({
      search,
      status: status || undefined,
      page: pagination.page,
      limit: pagination.limit,
    })
      .then(({ data }) => {
        if (!mounted) return;

        const items = Array.isArray(data?.data) ? data.data : [];
        setTournaments(items);

        setPagination((prev) => ({
          ...prev,
          total: data?.meta?.total ?? items.length,
          totalPages: data?.meta?.totalPages ?? 1,
        }));
      })
      .catch(() => {
        if (mounted) {
          setTournaments([]);
          setPagination((prev) => ({ ...prev, total: 0, totalPages: 1 }));
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [search, status, pagination.page, pagination.limit]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusChange = (value) => {
    setStatus(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page }));
    }
  };

  const totalLabel = useMemo(
    () => (pagination.total || 0).toLocaleString("fa-IR"),
    [pagination.total]
  );

  return (
    <main className="min-h-screen overflow-hidden">
      {/* ===================== HERO ===================== */}
      <section className="hero-panel relative isolate mx-3 overflow-hidden rounded-[2.25rem] bg-[#07150F] shadow-[0_50px_100px_-40px_rgba(7,21,15,0.7)] sm:mx-5 sm:rounded-[3rem]">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-lime-500/10 blur-3xl" />
        <div className="pointer-events-none absolute left-1/2 top-0 h-px w-full -translate-x-1/2 bg-gradient-to-l from-transparent via-white/20 to-transparent" />

        <div className="container-page relative py-20 sm:py-24 lg:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-xs font-black text-emerald-300 backdrop-blur">
              <Icon name="trophy" className="h-4 w-4" />
              رقابت‌های رسمی هیئت فوتبال شهر
            </span>

            <h1 className="mt-6 text-4xl font-black leading-[1.15] tracking-tight text-white sm:text-5xl lg:text-6xl">
              تورنمنت‌ها و
              <span className="mt-1 block bg-gradient-to-l from-emerald-300 via-emerald-200 to-lime-300 bg-clip-text text-transparent">
                مسابقات فصل
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-sm leading-8 text-white/55 sm:text-base">
              همه‌ی تورنمنت‌ های در حال برگزاری، آینده و پایان‌ یافته را در یک
              نگاه ببینید؛ روی هرکدام بزنید تا جدول رده‌ بندی، برنامه مسابقات
              و باشگاه‌های حاضر در آن را مشاهده کنید.
            </p>
          </div>
        </div>
      </section>

      {/* ===================== CONTENT ===================== */}
      <section className="container-page relative py-12 sm:py-16">
        {/* Filter panel */}
        <div className="relative z-10 -mt-24 mb-10 rounded-[2rem] border border-slate-200/80 bg-white/95 p-4 shadow-2xl shadow-slate-900/[0.12] backdrop-blur-xl sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <Input
                value={search}
                onChange={handleSearch}
                placeholder="جستجوی نام تورنمنت..."
                className="h-13 pr-12"
              />
              <Icon
                name="search"
                className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {STATUS_FILTERS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handleStatusChange(item.value)}
                  className={`whitespace-nowrap rounded-xl px-4 py-2.5 text-xs font-black transition ${
                    status === item.value
                      ? "bg-[#0B1F17] text-white shadow-lg"
                      : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Heading */}
        {!loading && tournaments.length > 0 && (
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="text-xs font-black text-emerald-600">
                فهرست تورنمنت‌ ها
              </span>
              <h2 className="mt-2 text-2xl font-black text-[#0B1F17] sm:text-3xl">
                {search ? `نتایج جستجو برای «${search}»` : "همه تورنمنت‌ ها"}
              </h2>
            </div>

            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-500 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              {totalLabel} تورنمنت
            </div>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="h-80 animate-pulse rounded-[2rem] bg-white shadow-sm"
              />
            ))}
          </div>
        ) : tournaments.length === 0 ? (
          <div className="rounded-[2.25rem] border border-dashed border-slate-200 bg-white px-6 py-20 text-center shadow-sm">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-[1.75rem] bg-slate-50 text-slate-300">
              <Icon name="trophy" className="h-10 w-10" />
            </div>

            <h2 className="mt-6 text-xl font-black text-slate-700">
              تورنمنتی پیدا نشد
            </h2>

            <p className="mx-auto mt-3 max-w-md text-sm leading-7 text-slate-400">
              {search
                ? `تورنمنتی با عبارت «${search}» پیدا نشد.`
                : "هنوز تورنمنتی برای نمایش در سامانه ثبت نشده است."}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {tournaments.map((tournament) => (
                <TournamentCard
                  key={tournament.id}
                  tournament={tournament}
                  onClick={() => navigate(`/tournaments/${tournament.id}`)}
                />
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div className="mt-12">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}